import { useMemo } from 'react';

import { FieldIssue } from '../api/apiTypes.shim';

export type FieldIssueProps = {
  issues: FieldIssue[];
  issuePath: string;
  /** @default false whether or not the issuePath should be used to match issues with a path that begins with the issuePath */
  matchPartial?: boolean;
};

type IssueFilter = (issue: FieldIssue) => boolean;

const getIssueFilter = ({
  issuePath,
  matchPartial
}: Pick<FieldIssueProps, 'issuePath' | 'matchPartial'>): IssueFilter => {
  if (matchPartial) {
    const splitIssuePath = issuePath.split('.');
    return (issue) =>
      splitIssuePath.every((keyProp, index) => issue.path[index] !== undefined && `${issue.path[index]}` === keyProp);
  } else {
    return (issue) => issue.path.join('.') === issuePath;
  }
};

export const getIssuesError = ({ issues, issuePath, matchPartial }: FieldIssueProps) => {
  if (!issues.length) return undefined;

  const issueFilterFn = getIssueFilter({ issuePath, matchPartial });
  return (
    issues
      .filter(issueFilterFn)
      .map(({ message }) => message)
      .join(', ') || undefined
  );
};

export const removeIssues = ({ issues, issuePath, matchPartial }: FieldIssueProps) => {
  if (!issues.length) return issues;

  const issueFilterFn = getIssueFilter({ issuePath, matchPartial });
  return issues.filter((issue) => !issueFilterFn(issue));
};

type UseErrorHookOptions = {
  error?: boolean;
  fieldIssueProps?: FieldIssueProps;
  helperText?: React.ReactNode;
};

export const useErrorProps = (opts: UseErrorHookOptions) => {
  return useMemo(() => {
    const mappedErrorMessage = opts?.fieldIssueProps && getIssuesError(opts.fieldIssueProps);
    return {
      error: !!(mappedErrorMessage || opts.error),
      helperText: mappedErrorMessage || opts.helperText
    };
    // only listening for issue changes in fieldIssueProps as the rest is static
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.fieldIssueProps?.issues, opts.error, opts.helperText]);
};

export type ClearIssueItem = string | Pick<FieldIssueProps, 'issuePath' | 'matchPartial'>;
